import { useEffect, useMemo, useState } from "react"
import { addDays, isSameDay, isToday, isValid, subDays } from "date-fns"
import classNames from "classnames"

import { Icon } from "components"
import { Text } from "ui-kit/components-branded/index"
import { Spinner } from "components/Spinner"
import { DatePickerError } from "modules/BookingCommon/common/interfaces"
import { twoWeeks } from "modules/BookingCommon/common/utils"
import { ServiceType } from "utils/helpers"
import { Client, OfferedService, Psychologist } from "utils/interfaces"
import { gaGetClientIdSync } from "utils/analytics"
import {
    analyticsEvents,
    preprocessAvailableHours,
    SessionLocalTimeSlot,
} from "modules/BookingCommon/components/DatePicker/utils"
import { FromToData } from "modules/BookingCommon/components/DatePicker/FromToData"
import { INITIATOR, weekDaysShort } from "modules/BookingCommon/components/DatePicker/constants"
import { getAvailableHours } from "modules/BookingCommon/components/DatePicker/api"
import { SelectedDayAvailableHours } from "modules/BookingCommon/components/DatePicker/SelectedDayAvailableHours"
import { useOnlyFreeSessions } from "pages/FreePsysPage/useOnlyFreeSessions"
import { UserAccount } from "utils/UserAccount"
import { format } from "utils/datetime-format-utils"
import * as utils from "utils/utils"

interface Props {
    psy: Psychologist
    datePickerIsLoaded: boolean
    currentWeek: Date | undefined
    selectedService: OfferedService | undefined
    initiator: INITIATOR
    psychologistClient?: Client
    onChangeSlotId: (slotId: string) => void
    onChangeSelectedTime: (time: Date) => void
    onChangeDatePickerErrorStatus: (error?: DatePickerError) => void
    onChangeDatePickerIsLoaded: (isLoaded: boolean) => void
    onChangeSlotDuration: (duration: number) => void
    onChangeCurrentWeek: (value: Date) => void
}

export function DatePicker({
    psy,
    datePickerIsLoaded,
    currentWeek,
    selectedService,
    initiator,
    psychologistClient,
    onChangeSlotId,
    onChangeSelectedTime,
    onChangeDatePickerErrorStatus,
    onChangeDatePickerIsLoaded,
    onChangeSlotDuration,
    onChangeCurrentWeek,
}: Props) {
    const onlyFree = useOnlyFreeSessions()
    const [selectedDayOfMonth, setSelectedDayOfMonth] = useState<Date>()
    const [daySlots, setDaySlots] = useState<SessionLocalTimeSlot[]>()
    const [slots, setSlots] = useState<Record<string, SessionLocalTimeSlot[]>>({})
    const [selectedSlot, setSelectedSlot] = useState<SessionLocalTimeSlot>()
    const [onlineSlotError, setOnlineSlotsError] = useState("")
    const [offlineSlotError, setOfflineSlotsError] = useState("")
    const [datePickerError, setDatePickerError] = useState("")

    useEffect(() => {
        if (!selectedDayOfMonth) {
            setDaySlots(undefined)
            return
        }
        const date = utils.formatLocalDate(selectedDayOfMonth)
        const daySlots = slots[date]
        if (!daySlots) return
        setDaySlots(daySlots)
        setSelectedSlot(daySlots[0])
    }, [selectedDayOfMonth, slots])

    useEffect(() => {
        if (selectedService?.online) {
            onChangeDatePickerErrorStatus(
                onlineSlotError
                    ? {
                          message: onlineSlotError,
                      }
                    : undefined
            )
            setDatePickerError(onlineSlotError)
        } else {
            onChangeDatePickerErrorStatus(
                offlineSlotError
                    ? {
                          message: offlineSlotError,
                      }
                    : undefined
            )
            setDatePickerError(offlineSlotError)
        }
    }, [onlineSlotError, offlineSlotError, selectedService?.online])

    const { twoWeeksData, from, to } = useMemo(() => {
        let twoWeeksData: Date[][] = []
        let from = null
        let to = null

        if (currentWeek) {
            twoWeeksData = twoWeeks(currentWeek)
            from = twoWeeksData[0][0]
            to = addDays(twoWeeksData[1][6], 1)
        }

        return {
            twoWeeksData,
            from,
            to,
        }
    }, [currentWeek])

    useEffect(() => {
        if (!selectedSlot) return
        onChangeSlotId(selectedSlot.id)
        onChangeSelectedTime(selectedSlot.sessionDateTime)
        onChangeSlotDuration(selectedSlot.duration)
    }, [selectedSlot])

    useEffect(() => {
        if (datePickerIsLoaded) {
            if (onlyFree) return
            analyticsEvents(
                Object.entries(slots)
                    .map(([_, val]) => val)
                    .flat()
                    .map((slot) => slot.sessionDateTime.toUTCString())
            )
        }
    }, [datePickerIsLoaded])

    useEffect(() => {
        if (from && to && selectedService) {
            fetchHours(from, to, selectedService)
        }
    }, [from?.toString(), to?.toString(), selectedService])

    function showError(message: string, online: boolean) {
        online ? setOnlineSlotsError(message) : setOfflineSlotsError(message)
        onChangeDatePickerIsLoaded(true)
    }

    function fetchHours(from: Date, to: Date, selectedService: OfferedService) {
        onChangeDatePickerIsLoaded(false)

        const options = {
            psy_hash_id: psy.hash_id,
            from_date: format(subDays(from, 1), "yyyy-MM-dd"),
            to_date: format(to, "yyyy-MM-dd"),
            offset_from_utc_minutes: -new Date().getTimezoneOffset(),
            service_type: new ServiceType(selectedService),
            for_psychologist: initiator === INITIATOR.PSYCHOLOGIST,
        }

        const additionalOptions =
            initiator === INITIATOR.CLIENT
                ? {
                      only_free: onlyFree,
                      client_data: UserAccount.data,
                      ga_client_id: gaGetClientIdSync(),
                  }
                : {
                      client_data: psychologistClient,
                  }

        getAvailableHours(options, additionalOptions)
            .then((data) => {
                if (Object.keys(data).length !== 0) {
                    const slots = preprocessAvailableHours(data)

                    const firstSlot = Object.values(slots)?.[0]?.[0]
                    setSelectedSlot(firstSlot)

                    const firstDate = Object.keys(slots)[0]

                    const parts = firstDate?.split(".").map(Number)
                    const formattedDate = parts
                        ? new Date(parts[2], parts[1] - 1, parts[0])
                        : undefined
                    setSelectedDayOfMonth(formattedDate)
                    setSlots(slots)
                    showError("", selectedService.online)
                } else {
                    showError("В этот период свободных дат нет.", selectedService.online)
                }
            })
            .catch(() => {
                showError(
                    "Не удалось загрузить календарь. Попробуйте обновить страницу.",
                    selectedService.online
                )
            })
            .finally(() => {
                onChangeDatePickerIsLoaded(true)
            })
    }

    function dayClasses(day: Date) {
        const dayHasHours = utils.formatLocalDate(day) in slots

        return classNames({
            "date-picker__day": true,
            "date-picker__day-today": isToday(day),
            "date-picker__day-has-available-hours": dayHasHours,
            "date-picker__day-no-available-hours": datePickerError || !dayHasHours,
            "date-picker__day-selected":
                !datePickerError && selectedDayOfMonth ? isSameDay(day, selectedDayOfMonth) : false,
            "date-picker__day-today-has-no-available-hours ":
                isToday(day) && (!dayHasHours || datePickerError),
        })
    }

    function selectSlot(slot: SessionLocalTimeSlot | undefined) {
        setSelectedSlot(slot)
    }

    function selectDayOfMonth(day: Date) {
        setSelectedDayOfMonth(day)
    }

    function changeLeftWeek() {
        if (currentWeek) {
            onChangeCurrentWeek(subDays(currentWeek, 14))
        }
    }

    function changeRightWeek() {
        if (currentWeek) {
            onChangeCurrentWeek(addDays(currentWeek, 14))
        }
    }

    if (!datePickerIsLoaded) {
        return (
            <div className="date-picker__spinner-container">
                <Spinner />
            </div>
        )
    }

    return (
        <div className="date-picker">
            <div className="date-picker__change-week">
                {from && isValid(from) && isValid(to) && (
                    <>
                        <Icon
                            onClick={changeLeftWeek}
                            className="mr-4"
                            type="arrow-left-date-picker"
                            size={24}
                            button
                        />
                        <FromToData from={from} to={addDays(from, 13)} />
                        <Icon
                            onClick={changeRightWeek}
                            type="arrow-right-date-picker"
                            size={24}
                            button
                        />
                    </>
                )}
            </div>
            <div className="date-picker__week-day-short-container">
                {weekDaysShort.map((day) => (
                    <div key={day} className="date-picker__week-day-short">
                        {day}
                    </div>
                ))}
            </div>

            {twoWeeksData.map((week, index) => (
                <div key={index} className="date-picker__week">
                    {week.map((day) => {
                        if (!isValid(day)) return null
                        return (
                            <div
                                key={day.getTime()}
                                onClick={() => selectDayOfMonth(day)}
                                className={dayClasses(day)}
                            >
                                {format(day, "d")}
                            </div>
                        )
                    })}
                </div>
            ))}
            {datePickerError && (
                <Text className="date-picker__error-message">{datePickerError}</Text>
            )}
            {!daySlots?.length && !datePickerIsLoaded && (
                <div className="alert alert-danger text-center" role="alert">
                    В&nbsp;этот день нет времени, доступного для записи. Выберите другую дату.
                </div>
            )}
            {!datePickerError && selectedDayOfMonth && daySlots && (
                <SelectedDayAvailableHours
                    slots={daySlots}
                    slot={selectedSlot}
                    OnSelect={selectSlot}
                />
            )}
        </div>
    )
}
